import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingLabel?: string;
  fullWidth?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-burgundy-500 text-cream-50 hover:bg-burgundy-600 disabled:hover:bg-burgundy-500 shadow-sm',
  secondary:
    'bg-cream-100 text-burgundy-500 hover:bg-cream-200 disabled:hover:bg-cream-100 ring-1 ring-burgundy-500/10',
  ghost: 'bg-transparent text-burgundy-500 hover:bg-cream-100 disabled:hover:bg-transparent',
  destructive: 'bg-heart text-cream-50 hover:opacity-90 disabled:hover:opacity-100',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm rounded-2xl',
  md: 'h-11 px-5 text-sm rounded-2xl',
  lg: 'h-12 px-6 text-base rounded-2xl',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingLabel,
    fullWidth = false,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center font-semibold transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variantStyles[variant],
        sizeStyles[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (loadingLabel ?? 'Working…') : children}
    </button>
  );
});
